import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { format } from "date-fns";
import { groupBy } from "lodash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlInvoice, GqlInvoicedProject } from "~/__generated__";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatCents, formatDateTime, formatSeconds } from "~/modules/app/util/formatters";

import { useInvoiceCsvForPeriod } from "../api/useInvoiceCsvForPeriod";

interface InvoiceProps {
    data: GqlInvoice;
}

export function Invoice({ data }: InvoiceProps) {
    const { t } = useTranslation("invoice");

    const { downloadCsv, loading } = useInvoiceCsvForPeriod();

    const handleDownload = () => {
        downloadCsv(data.id);
    };

    return (
        <GaiaPageGridComponent
            title={t("detail.invoiceForPeriod", {
                period: format(parseStringToDate(data.periodEnd), "MMMM yyyy"),
            })}
        >
            <Box sx={{ px: 1 }}>
                <Typography variant="subtitle2">{t("detail.totalCostPeriod")}</Typography>
                <Typography variant="subtitle1">{formatCents(data.totalCostInCents)}</Typography>
            </Box>
            <Button onClick={handleDownload} loading={loading}>
                {t("detail.btnDownloadCsv")}
            </Button>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>{t("detail.project")}</TableCell>
                            <TableCell align="right">{t("detail.amount")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.projects.map(project => (
                            <InvoiceTableItem key={project.projectId} project={project} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </GaiaPageGridComponent>
    );
}

interface InvoiceTableItemProps {
    project: GqlInvoicedProject;
}

function InvoiceTableItem({ project }: InvoiceTableItemProps) {
    const { t } = useTranslation("invoice");
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <>
            <TableRow>
                <TableCell>
                    <IconButton onClick={() => setIsExpanded(!isExpanded)} size="small">
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell>{project.name}</TableCell>
                <TableCell align="right">{formatCents(project.costInCents)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={3} sx={{ paddingY: 1 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography gutterBottom variant="h6">
                                {t("detail.visit.completedVisits")}
                            </Typography>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("detail.visit.date")}</TableCell>
                                        <TableCell>{t("detail.visit.protocolName")}</TableCell>
                                        <TableCell>{t("detail.visit.visitNumber")}</TableCell>
                                        <TableCell>{t("detail.visit.travelTime")}</TableCell>
                                        <TableCell>{t("detail.visit.travelDistance")}</TableCell>
                                        <TableCell>{t("detail.visit.travelCostPerKm")}</TableCell>
                                        <TableCell>{t("detail.visit.requiredFieldworkers")}</TableCell>
                                        <TableCell>{t("detail.visit.pricePerHour")}</TableCell>
                                        <TableCell>{t("detail.visit.duration")}</TableCell>
                                        <TableCell align="right">{t("detail.visit.cost")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(groupBy(project.visits, visit => visit.plannerAreaName)).map(
                                        ([areaName, visits]) => (
                                            <React.Fragment key={areaName}>
                                                <TableRow>
                                                    <TableCell colSpan={10}>{areaName}</TableCell>
                                                </TableRow>
                                                {visits.map(visit => (
                                                    <TableRow key={visit.plannerVisitId}>
                                                        <TableCell>
                                                            {formatDateTime(parseStringToDate(visit.date))}
                                                        </TableCell>
                                                        <TableCell>{visit.protocolName}</TableCell>
                                                        <TableCell>{visit.protocolVisitNumber}</TableCell>
                                                        <TableCell>
                                                            {formatSeconds(visit.travelAllowanceTimeInSeconds)}
                                                        </TableCell>
                                                        <TableCell>{visit.travelAllowanceKms} km</TableCell>
                                                        <TableCell>
                                                            {formatCents(visit.travelAllowancePerKmInCents)}
                                                        </TableCell>
                                                        <TableCell>{visit.requiredFieldworkers}</TableCell>
                                                        <TableCell>
                                                            {formatCents(visit.pricePerHourInCents)}
                                                        </TableCell>
                                                        <TableCell>{formatSeconds(visit.durationInSeconds)}</TableCell>
                                                        <TableCell align="right">
                                                            {formatCents(visit.costInCents)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ),
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}
